import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, QrCode, Search, Check } from "lucide-react";
import AdminShell from "../../components/admin/AdminShell";
import { Overline } from "../../components/Layout";
import { api } from "../../lib/api";

export default function AdminEventCheckin() {
  const { id } = useParams();
  const [ev, setEv] = useState(null);
  const [regs, setRegs] = useState([]);
  const [qr, setQr] = useState("");
  const [q, setQ] = useState("");
  const [showTypeahead, setShowTypeahead] = useState(false);

  const loadEv = () => api.get(`/events/${id}`).then((r) => setEv(r.data));
  const loadRegs = () => api.get(`/events/${id}/registrations`).then((r) => setRegs(r.data));
  useEffect(() => { loadEv(); loadRegs(); }, [id]);

  const checkin = async (code) => {
    try {
      const r = await api.post(`/events/${id}/checkin`, null, { params: { qr: code } });
      toast.success(`Checked in: ${r.data.name}`);
      loadRegs();
      setQr("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Not found");
    }
  };

  const filtered = regs.filter((r) => !q || `${r.name} ${r.email} ${r.qr_code}`.toLowerCase().includes(q.toLowerCase()));
  const checkedIn = regs.filter((r) => r.checked_in).length;
  const total = regs.filter((r) => r.status === "going" || r.status === "checked_in").length;

  if (!ev) return null;

  return (
    <AdminShell testId="admin-checkin-page">
      <div className="max-w-4xl">
        <Link to={`/admin/events/${id}/manage`} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1.5 mb-4">
          <ArrowLeft size={14} /> Back to event
        </Link>
        <Overline>Live check-in</Overline>
        <h1 className="mt-2 text-4xl font-heading font-medium tracking-tight">{ev.title}</h1>

        <div className="mt-8 grid md:grid-cols-[1fr_320px] gap-6">
          <div className="card-hair p-6">
            <div className="flex items-center gap-2">
              <QrCode size={18} style={{ color: "hsl(var(--vd-blue))" }} />
              <Overline>Scan or type QR</Overline>
            </div>
            <div className="mt-3 flex gap-2">
              <input className="input-field font-mono" placeholder="vd_reg_…" value={qr} onChange={(e) => setQr(e.target.value)}
                     onKeyDown={(e) => { if (e.key === "Enter") checkin(qr); }} data-testid="checkin-input" />
              <button onClick={() => checkin(qr)} className="btn-primary" data-testid="checkin-submit">Check in</button>
            </div>

            <div className="mt-6 relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                className="input-field pl-9"
                placeholder="Type a guest name to check in…"
                value={q}
                onChange={(e) => { setQ(e.target.value); setShowTypeahead(true); }}
                onFocus={() => setShowTypeahead(true)}
                onBlur={() => setTimeout(() => setShowTypeahead(false), 150)}
                data-testid="checkin-search"
              />
              {showTypeahead && q.trim() && (
                <div className="absolute left-0 right-0 top-full mt-1.5 max-h-64 overflow-y-auto glass-panel z-20" data-testid="checkin-typeahead">
                  {filtered.slice(0, 8).length === 0 ? (
                    <div className="p-3 text-sm text-muted-foreground text-center">No matching guests.</div>
                  ) : filtered.slice(0, 8).map((r) => (
                    <button
                      key={r.id}
                      onMouseDown={(e) => { e.preventDefault(); checkin(r.qr_code); setQ(""); setShowTypeahead(false); }}
                      className="w-full text-left p-3 flex items-center justify-between hover:bg-white/60 transition"
                      data-testid={`typeahead-${r.id}`}
                    >
                      <div>
                        <div className="text-sm font-medium">{r.name}</div>
                        <div className="text-xs text-muted-foreground">{r.email}</div>
                      </div>
                      {r.checked_in ? (
                        <span className="chip chip-purple text-[10px]">Checked in</span>
                      ) : (
                        <span className="chip chip-green text-[10px]">Check in</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-4 max-h-[420px] overflow-y-auto divide-y divide-border rounded-2xl border border-border">
              {filtered.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No matches.</div>}
              {filtered.map((r) => (
                <div key={r.id} className="p-3 flex items-center justify-between" data-testid={`checkin-row-${r.id}`}>
                  <div>
                    <div className="text-sm font-medium">{r.name}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{r.qr_code}</div>
                  </div>
                  {r.checked_in ? (
                    <span className="chip chip-purple text-[10px]">Checked in</span>
                  ) : (
                    <button onClick={() => checkin(r.qr_code)} className="btn-ghost text-xs h-8 px-3"><Check size={12} />In</button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="card-cream p-6 text-center">
              <div className="overline">Live attendance</div>
              <div className="mt-3 stat-value text-6xl" style={{ color: "hsl(var(--vd-orange-ink))" }}>{checkedIn}</div>
              <div className="text-sm text-muted-foreground mt-1">of {total} going</div>
            </div>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
